import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CARRIER_CHANGE_REQUEST_STATUS_LABELS,
  CARRIER_CHANGE_REQUEST_TYPE_LABELS,
  type CarrierChangeRequestStatus,
  type CarrierChangeRequestType,
} from "@aam/shared";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { PageHeader } from "./ui/PageHeader";
import { LoadingStatus, Skeleton, SkeletonBadge, SkeletonText } from "./ui/Skeleton";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface QueueRow {
  id: string;
  requestType: CarrierChangeRequestType;
  status: CarrierChangeRequestStatus;
  createdAt: string;
  sentAt: string | null;
  receiptReceivedAt: string | null;
  followUpDueAt: string | null;
  emailSubject: string | null;
  formData: Record<string, unknown>;
  agentUser: { id: string; email: string; firstName: string | null; lastName: string | null };
}

function formatWhen(value: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function agentLabel(agent: QueueRow["agentUser"]) {
  const name = [agent.firstName, agent.lastName].filter(Boolean).join(" ");
  return name || agent.email;
}

export function CarrierChangeRequestQueue() {
  const token = getSessionToken();
  const [requests, setRequests] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadQueue() {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/carrier-change-requests/queue?carrier=transamerica`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to load queue");
      setRequests(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }

  async function patchRequest(requestId: string, body: Record<string, unknown>) {
    if (!token) return;
    setActionId(requestId);
    try {
      const res = await fetch(`${API_BASE}/api/carrier-change-requests/${requestId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update request");
      await loadQueue();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update request");
    } finally {
      setActionId(null);
    }
  }

  return (
    <section className="space-y-5">
      <PageHeader
        title="Transamerica change requests"
        description="Track hierarchy changes awaiting carrier receipt, follow-up, or completion."
        actions={<Button size="sm" variant="secondary" onClick={() => void loadQueue()}>Refresh</Button>}
      />

      {loading && (
        <div role="status" aria-busy="true" className="grid gap-4 xl:grid-cols-2">
          <LoadingStatus label="Loading change requests" />
          {Array.from({ length: 4 }).map((_, index) => (
            <article
              key={index}
              aria-hidden="true"
              className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-2">
                  <SkeletonText width="w-44" />
                  <SkeletonText width="w-56" />
                  <SkeletonText width="w-36" />
                </div>
                <SkeletonBadge />
              </div>
              <div className="grid gap-3 rounded-lg border border-border-subtle bg-surface-overlay p-3 sm:grid-cols-2">
                <SkeletonText />
                <SkeletonText width="w-4/5" />
                <SkeletonText width="w-3/4" />
                <SkeletonText width="w-2/3" />
              </div>
              <div className="flex gap-2 border-t border-border-subtle pt-4">
                <Skeleton className="h-11 w-36" />
                <Skeleton className="h-11 w-24" />
              </div>
            </article>
          ))}
        </div>
      )}
      {error && <div role="alert" className="rounded-lg border border-status-rejected-border bg-status-rejected-bg p-4 text-sm text-status-rejected-text">{error}</div>}

      {!loading && requests.length === 0 && (
        <div className="rounded-[var(--card-radius)] border border-dashed border-border-default bg-surface-raised p-10 text-center">
          <p className="font-medium text-content-primary">Queue clear</p>
          <p className="mt-1 text-sm text-content-muted">No open Transamerica change requests.</p>
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        {requests.map((request) => (
          <article key={request.id} className="space-y-4 rounded-[var(--card-radius)] border border-border-default bg-surface-raised p-5 shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-content-primary">
                  {CARRIER_CHANGE_REQUEST_TYPE_LABELS[request.requestType]}
                </p>
                <p className="mt-1 text-xs text-content-secondary">
                  Agent:{" "}
                  <Link
                    to={`/dashboard/agents/${request.agentUser.id}`}
                    className="text-accent hover:opacity-80"
                  >
                    {agentLabel(request.agentUser)}
                  </Link>
                </p>
                <p className="text-xs text-content-muted">Created {formatWhen(request.createdAt)}</p>
              </div>
              <span className="rounded-md border border-status-pending-border bg-status-pending-bg px-2.5 py-1 text-xs font-semibold text-status-pending-text">
                {CARRIER_CHANGE_REQUEST_STATUS_LABELS[request.status]}
              </span>
            </div>

            <dl className="grid gap-2 rounded-lg border border-border-subtle bg-surface-overlay p-3 text-xs text-content-secondary sm:grid-cols-2">
              {request.emailSubject && (
                <div className="sm:col-span-2">
                  <dt className="font-medium text-content-muted">Subject</dt>
                  <dd className="mt-0.5 text-content-primary">{request.emailSubject}</dd>
                </div>
              )}
              {request.sentAt && (
                <div className="flex gap-2">
                  <dt className="shrink-0 text-content-muted">Sent:</dt>
                  <dd>{formatWhen(request.sentAt)}</dd>
                </div>
              )}
              {request.receiptReceivedAt && (
                <div className="flex gap-2">
                  <dt className="shrink-0 text-content-muted">Receipt:</dt>
                  <dd>{formatWhen(request.receiptReceivedAt)}</dd>
                </div>
              )}
              {request.followUpDueAt && (
                <div className="flex gap-2">
                  <dt className="shrink-0 text-content-muted">Follow-up due:</dt>
                  <dd className="text-status-pending-text">{formatWhen(request.followUpDueAt)}</dd>
                </div>
              )}
              {Object.entries(request.formData).map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <dt className="shrink-0 text-content-muted">{key}:</dt>
                  <dd className="break-words text-content-primary">{String(value ?? "—")}</dd>
                </div>
              ))}
            </dl>

            <div className="flex flex-wrap gap-2 border-t border-border-subtle pt-4">
              {request.status === "SUBMITTED" && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={actionId === request.id}
                  onClick={() => patchRequest(request.id, { markReceiptReceived: true })}
                >
                  Mark receipt received
                </Button>
              )}
              {(request.status === "EMAIL_RECEIVED" || request.status === "FOLLOW_UP_DUE") && (
                <Button
                  type="button"
                  size="sm"
                  disabled={actionId === request.id}
                  onClick={() => patchRequest(request.id, { status: "COMPLETED" })}
                >
                  Mark completed
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={actionId === request.id}
                onClick={() => patchRequest(request.id, { status: "CANCELLED" })}
              >
                Cancel
              </Button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
